<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Stripe Connect</span>
          <h2 class="workspace-title">Cuentas conectadas y payouts</h2>
          <p class="workspace-copy">Admin FOMO controla el estado tecnico, el modo de payout, on_behalf_of y la sincronizacion con Stripe.</p>
        </div>
        <button class="workspace-button workspace-button--primary" type="button" :disabled="loading" @click="loadAccounts">{{ loading ? 'Sincronizando...' : 'Actualizar' }}</button>
      </div>

      <div class="workspace-metrics connect-metrics">
        <article class="workspace-metric"><small>Cuentas</small><strong>{{ accounts.length }}</strong></article>
        <article class="workspace-metric"><small>Listas</small><strong>{{ readyAccounts }}</strong></article>
        <article class="workspace-metric"><small>Pendientes</small><strong>{{ pendingAccounts }}</strong></article>
        <article class="workspace-metric"><small>Restringidas</small><strong>{{ restrictedAccounts }}</strong></article>
      </div>

      <div v-if="feedback" :class="['workspace-feedback', feedbackType === 'error' ? 'workspace-feedback--error' : 'workspace-feedback--success']">{{ feedback }}</div>
      <div v-if="loading" class="workspace-empty">Cargando cuentas conectadas...</div>
      <div v-else-if="!accounts.length" class="workspace-empty">No hay eventos con Stripe Connect activo ni cuentas creadas.</div>

      <div v-else class="connect-account-grid">
        <article v-for="account in accounts" :key="account.organizer_id" class="connect-account-card">
          <div class="connect-account-card__head">
            <div>
              <span class="workspace-kicker">Organizer #{{ account.organizer_id }}</span>
              <h3>{{ account.organizer_name }}</h3>
              <p>{{ account.organizer_email }}</p>
            </div>
            <span :class="['workspace-status-pill', accountStatusClass(account)]">{{ accountStatusLabel(account) }}</span>
          </div>

          <div class="connect-account-card__meta">
            <span>Account: {{ account.profile.account_id || 'sin cuenta' }}</span>
            <span>Charges: {{ account.profile.charges_enabled ? 'on' : 'off' }}</span>
            <span>Payouts: {{ account.profile.payouts_enabled ? 'on' : 'off' }}</span>
            <span>Pais: {{ account.profile.country || 'N/A' }}</span>
          </div>

          <div v-if="account.events && account.events.length" class="connect-events">
            <span v-for="event in account.events" :key="event.id" class="workspace-badge">{{ event.name }}</span>
          </div>

          <div class="workspace-form workspace-form--two connect-account-card__form">
            <label class="workspace-field">
              <span>Modo payout</span>
              <select v-model="account.edit.payout_mode" class="workspace-input workspace-select">
                <option value="manual">Manual</option>
                <option value="fomo_scheduled">FOMO scheduled</option>
                <option value="stripe_automatic">Stripe automatic</option>
              </select>
            </label>
            <label class="workspace-field connect-toggle-field">
              <span>on_behalf_of</span>
              <input v-model="account.edit.on_behalf_of_enabled" type="checkbox" />
            </label>
            <label class="workspace-field workspace-field--wide">
              <span>Schedule JSON</span>
              <textarea v-model="account.edit.payout_schedule_json" class="workspace-input workspace-input--textarea" rows="3" placeholder='{"weekday":"friday"}'></textarea>
            </label>
          </div>

          <small v-if="account.profile.disabled_reason" class="connect-account-card__warning">{{ account.profile.disabled_reason }}</small>

          <div class="workspace-actions workspace-actions--end connect-actions">
            <button v-if="account.dashboard_url" class="workspace-button workspace-button--small" type="button" @click="openDashboard(account)">Dashboard</button>
            <button class="workspace-button workspace-button--small" type="button" :disabled="account.saving" @click="syncAccount(account)">Sync</button>
            <button class="workspace-button workspace-button--primary workspace-button--small" type="button" :disabled="account.saving" @click="saveAccount(account)">{{ account.saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </article>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'

export default {
  name: 'StripeConnectOpsView',
  data() {
    return {
      accounts: [],
      loading: false,
      feedback: '',
      feedbackType: 'success',
    }
  },
  computed: {
    readyAccounts() { return this.accounts.filter((account) => account.profile && account.profile.ready_to_charge_card).length },
    pendingAccounts() { return this.accounts.filter((account) => account.profile && account.profile.status === 'pending').length },
    restrictedAccounts() { return this.accounts.filter((account) => account.profile && account.profile.status === 'restricted').length },
  },
  created() {
    this.loadAccounts()
  },
  methods: {
    normalizeAccount(account) {
      const profile = account.profile || {}
      return {
        ...account,
        saving: false,
        edit: {
          payout_mode: profile.payout_mode || 'manual',
          on_behalf_of_enabled: Boolean(profile.on_behalf_of_enabled),
          payout_schedule_json: JSON.stringify(profile.payout_schedule || {}, null, 2),
        },
      }
    },
    loadAccounts(clearFeedback = true) {
      this.loading = true
      if (clearFeedback) this.feedback = ''
      return apiService('/api/admin/connect/accounts/').then((data) => {
        if (!Array.isArray(data)) {
          this.accounts = []
          this.feedback = (data && (data.err || data.detail)) || 'No pudimos cargar Stripe Connect.'
          this.feedbackType = 'error'
          return
        }
        this.accounts = data.map((account) => this.normalizeAccount(account))
      }).catch(() => {
        this.feedback = 'No pudimos cargar Stripe Connect.'
        this.feedbackType = 'error'
      }).finally(() => {
        this.loading = false
      })
    },
    accountStatusClass(account) {
      if (account.profile && account.profile.ready_to_charge_card) return 'is-active'
      return 'is-warning'
    },
    accountStatusLabel(account) {
      const profile = account.profile || {}
      if (profile.ready_to_charge_card) return 'Ready'
      if (profile.status === 'restricted') return 'Restricted'
      if (profile.status === 'pending') return 'Pending'
      return 'Not started'
    },
    parseSchedule(account) {
      const raw = String(account.edit.payout_schedule_json || '').trim()
      if (!raw) return {}
      return JSON.parse(raw)
    },
    async saveAccount(account) {
      account.saving = true
      this.feedback = ''
      let payoutSchedule = {}
      try {
        payoutSchedule = this.parseSchedule(account)
      } catch (_error) {
        this.feedback = 'Schedule JSON invalido.'
        this.feedbackType = 'error'
        account.saving = false
        return
      }
      const payload = {
        payout_mode: account.edit.payout_mode,
        on_behalf_of_enabled: account.edit.on_behalf_of_enabled,
        payout_schedule: payoutSchedule,
      }
      const data = await apiService(`/api/admin/connect/accounts/${account.organizer_id}/`, 'PATCH', payload)
      account.saving = false
      if (data && !data.err && !data.detail) {
        this.feedback = `Configuracion guardada para ${account.organizer_email}.`
        this.feedbackType = 'success'
        await this.loadAccounts(false)
        return
      }
      this.feedback = (data && (data.err || data.detail)) || 'No pudimos guardar la configuracion.'
      this.feedbackType = 'error'
    },
    async syncAccount(account) {
      account.saving = true
      const data = await apiService(`/api/admin/connect/accounts/${account.organizer_id}/`, 'POST', {})
      account.saving = false
      if (data && !data.err && !data.detail) {
        this.feedback = `Cuenta sincronizada para ${account.organizer_email}.`
        this.feedbackType = 'success'
        await this.loadAccounts(false)
        return
      }
      this.feedback = (data && (data.err || data.detail)) || 'No pudimos sincronizar la cuenta.'
      this.feedbackType = 'error'
    },
    openDashboard(account) {
      window.open(account.dashboard_url, '_blank')
    },
  },
}
</script>

<style scoped>
.connect-metrics {
  margin-top: 14px;
}

.connect-account-grid {
  display: grid;
  gap: 16px;
}

.connect-account-card {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(15, 23, 42, 0.1);
}

.connect-account-card__head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}

.connect-account-card__head h3,
.connect-account-card__head p {
  margin: 0;
}

.connect-account-card__meta,
.connect-events {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.connect-account-card__meta span {
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
  font-size: 0.82rem;
}

.connect-account-card__form {
  margin-top: 4px;
}

.connect-toggle-field input {
  width: 22px;
  height: 22px;
}

.connect-account-card__warning {
  color: #7c2d12;
}

.connect-actions {
  justify-content: flex-end;
}
</style>
